import React, { useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import styles from "./DueDateCard.module.scss";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { formatDate } from "../../utility/functions/common-functions";

interface DueDateCardProps {
  recentDeliveryDueDate: string;
  actualDeliveryDate: string | null; // null if not delivered
  enrollmentDate: string;
  duration: number;
  durationText: string;
}

const DueDateCard: React.FC<DueDateCardProps> = ({
  recentDeliveryDueDate,
  actualDeliveryDate,
  enrollmentDate,
  duration,
  durationText,
}) => {
  const { t } = useTranslation();
  const hasDelivered = !!actualDeliveryDate;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const displayDate = hasDelivered
    ? actualDeliveryDate!
    : recentDeliveryDueDate;
  const label = hasDelivered ? t("yourDeliveryDate") : t("yourDueDate");

  const daysLeft = useMemo(() => {
    const today = dayjs();
    const dueDate = dayjs(displayDate);
    return today.isBefore(dueDate) ? dueDate.diff(today, "day") : 0;
  }, [displayDate]);

  const percentage = (1 - daysLeft / 280) * 100;

  return (
    <Box className={styles.dueDateCard}>
      <Box className={styles.content}>
        <Box>
          <Typography
            variant={isMobile ? "bodySmall" : "subtitle1"}
            component="h4"
            color={isMobile ? "secondary.main" : "primary"}
            mb={1}
            fontWeight={700}
            textTransform={isMobile ? "uppercase" : "none"}
          >
            {label}
          </Typography>
          <Typography variant="h4" color="black" mb={2}>
            {displayDate ? formatDate(displayDate) : "--"}
          </Typography>
          <Typography variant="bodySmall" color="#666">
            {t("enrolledOn")} {enrollmentDate}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Box position="relative" display="inline-flex">
            <CircularProgress
              variant="determinate"
              value={100}
              size={80}
              thickness={4.5}
              className={styles.circularBg}
              sx={{ color: "#fff" }}
            />
            <CircularProgress
              variant="determinate"
              value={percentage}
              size={80}
              thickness={4.5}
              className={styles.circularProgress}
              sx={{ color: "#A9C2B1" }}
            />
            <Box className={styles.circularCenter}>
              <Typography variant="subtitle1">{duration}</Typography>
            </Box>
          </Box>

          <Typography className={styles.daysLeftText} variant="body1">
            {durationText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DueDateCard;
